'use client';

import {
  ButtonSize,
  FsButton,
  FsInputText,
} from '@printi/ds-offset-react-core';
import { useLayout } from '@mf/common/components/Media/Layout';
import { useValidationHooks } from '@mf/common/utils/hooks/index';
import { useEffect, useRef, useState } from 'react';
import { Heading, HeadingType } from '../Heading';
import { TermsModal } from '../TermsModal';
import * as S from './PhysicalPerson.styles';
import { usePhysicalPersonHooks } from './PhysicalPerson.hooks';

export const PhysicalPerson = () => {
  const { isMobile } = useLayout();
  const {
    formik,
    isLoading,
    verifyPhoneMask,
    isLoadingSocialRegister,
    isSocialRegister,
    isCompleteRegister,
    customerData,
    isLoadingCompleteRegister,
    provider,
  } = usePhysicalPersonHooks();
  const { isValidFields, setIsValidFields, cpfField } = useValidationHooks();
  const {
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = formik;

  const termModalType = useRef('');
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const formattedBirthdate = (date: string) => {
    return date.split('-').reverse().join('/') as string;
  };

  const handleValidation = () => {
    const inputs = {
      cpf: values.cpf,
      phone: values.phone,
      birthdate: values.birthdate,
    };
    const cpfIsValid = cpfField(values.cpf);

    const isFormFilled = Object.values(inputs).every(
      (value) => value?.length > 0,
    );

    const emailConsentIsFine = !provider ? true : values.emailConsent;

    // Todos os três checkboxes são obrigatórios
    const areRequiredCheckboxesChecked =
      values.termsAccepted && values.privacyAccepted && emailConsentIsFine;

    const isValid = isFormFilled && cpfIsValid && areRequiredCheckboxesChecked;

    setIsValidFields(isValid);
  };

  useEffect(() => {
    if (isCompleteRegister()) {
      setFieldValue('cpf', customerData?.cpf || '');
      setFieldValue('phone', customerData?.phone || '');
      setFieldValue(
        'birthdate',
        formattedBirthdate(customerData?.birthdate || ''),
      );
      setFieldValue(
        'receiveOffers',
        customerData?.exclusive_offers_subscribe === '1',
      );
    }
  }, [isCompleteRegister, customerData, setFieldValue]);

  useEffect(() => {
    handleValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.termsAccepted, values.privacyAccepted, values.emailConsent]);

  const handleCheckboxChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      setFieldValue(field, isChecked);

      if (isChecked) {
        setFieldValue(`${field}AcceptedAt`, new Date().toISOString());
      } else {
        setFieldValue(`${field}AcceptedAt`, null);
      }
    };

  const handleOpenTermsModal = (type: string) => {
    termModalType.current = type;
    setIsTermsModalOpen(true);
  };

  return (
    <>
      <form>
        <Heading
          type={HeadingType.HeadingHeadingDescription}
          heading2XS="Pessoa Física"
          heading="Complete seus dados"
          description="Para ativar sua conta para compras no site."
        />
        <S.Base>
          <FsInputText
            value={values.cpf}
            id="cpf"
            label="CPF"
            placeholder="000.000.000-00"
            supportText={errors.cpf}
            error={!!errors.cpf}
            maskType={'cpf'}
            onChange={(e) => {
              handleChange(e);
              handleValidation();
            }}
            onBlur={(e) => {
              handleBlur(e);
              handleValidation();
            }}
          />
          <S.InputGroup>
            <S.StructureInput>
              <FsInputText
                value={values.phone}
                id="phone"
                label="Telefone principal"
                placeholder="(00) 00000-0000"
                supportText={errors.phone}
                error={!!errors.phone}
                maskType={verifyPhoneMask(values.phone)}
                onChange={(e) => {
                  handleChange(e);
                  handleValidation();
                }}
                onBlur={(e) => {
                  handleBlur(e);
                  handleValidation();
                }}
              />
              <FsInputText
                id="birthdate"
                label="Data de Nascimento"
                placeholder="DD/MM/AAAA"
                supportText={errors.birthdate}
                error={!!errors.birthdate}
                value={values.birthdate}
                maskType={'birthDate'}
                onChange={(e) => {
                  handleChange(e);
                  handleValidation();
                }}
                onBlur={(e) => {
                  handleBlur(e);
                  handleValidation();
                }}
              />
            </S.StructureInput>
          </S.InputGroup>
          <S.CheckboxGroup
            inputProps={{
              id: 'receiveOffers',
            }}
            selected={values.receiveOffers}
            onChange={handleCheckboxChange('receiveOffers')}
            style={{
              justifyContent: 'left',
            }}
          >
            Receber ofertas exclusivas e novidades por e-mail
          </S.CheckboxGroup>

          {provider && (
            <S.CheckboxGroup
              inputProps={{
                id: 'emailConsent',
                required: true,
              }}
              selected={values.emailConsent}
              onChange={handleCheckboxChange('emailConsent')}
              style={{
                justifyContent: 'left',
              }}
            >
              <span>
                Autorizo a Printi a usar meu e-mail do{' '}
                <S.CheckboxGroupText>{provider}</S.CheckboxGroupText> para
                enviar atualizações sobre meus pedidos.
              </span>
            </S.CheckboxGroup>
          )}

          <S.ChecboxGroupBox>
            <S.CheckboxGroup
              inputProps={{
                id: 'termsAccepted',
                required: true,
              }}
              selected={values.termsAccepted}
              onChange={handleCheckboxChange('termsAccepted')}
              style={{
                justifyContent: 'left',
              }}
            >
              Concordo com os
            </S.CheckboxGroup>
            <S.CheckboxGroupLink onClick={() => handleOpenTermsModal('terms')}>
              Termos de uso
            </S.CheckboxGroupLink>
          </S.ChecboxGroupBox>

          <S.ChecboxGroupBox>
            <S.CheckboxGroup
              inputProps={{
                id: 'privacyAccepted',
                required: true,
              }}
              selected={values.privacyAccepted}
              onChange={handleCheckboxChange('privacyAccepted')}
              style={{
                justifyContent: 'left',
              }}
            >
              Concordo com a
            </S.CheckboxGroup>
            <S.CheckboxGroupLink
              onClick={() => handleOpenTermsModal('privacy')}
            >
              Política de Privacidade.
            </S.CheckboxGroupLink>
          </S.ChecboxGroupBox>
        </S.Base>
        <FsButton
          size={isMobile ? ButtonSize.SM : ButtonSize.LG}
          type="button"
          onClick={() => handleSubmit()}
          disabled={!isValidFields}
          loading={
            isLoading || isLoadingSocialRegister || isLoadingCompleteRegister
          }
        >
          {isSocialRegister() || isCompleteRegister() ? 'Concluir' : 'Avançar'}
        </FsButton>
      </form>

      <TermsModal
        type={termModalType.current}
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </>
  );
};
